const ROOT_SELECTOR = '.cm_tabs';

const VEHICLE_TAB_CLASS = 'cm_vehicle-search';
const TIRE_TAB_CLASS = 'cm_tire-search';
const WHEEL_TAB_CLASS = 'cm_wheel-search';

const TIRE_STORAGE_KEY = 'cm_tire-tab-selection';
const WHEEL_STORAGE_KEY = 'cm_wheel-tab-selection';
const VEHICLE_STORAGE_KEY = 'cm_variant-model';

let currentTabsManager = null;

class ConvermaxYmmTabsInstance {
  constructor(rootElement, options) {
    this.rootElement = rootElement;
    this.options = options;

    this.tabSelectElement = this.rootElement.querySelector('.cm_tab-select');
    this.tabContentElement = this.rootElement.querySelector('.cm_tabs_content');
    this.tabButtonElements = [...this.rootElement.querySelectorAll('.cm_tab-buttons .cm_tab-button')];
  }

  init(initialTabClass) {
    if (!this.tabSelectElement || !this.tabContentElement) {
      return;
    }

    this.setActiveTab(initialTabClass, {
      updateSelect: true,
      syncGroup: false,
    });

    this.bindSelectChange();
    this.bindButtonClicks();
  }

  bindSelectChange() {
    this.tabSelectElement.addEventListener('change', (event) => {
      const selectedTabClass = event.target.value;

      this.setActiveTab(selectedTabClass, {
        updateSelect: false,
        syncGroup: true,
      });

      this.handleTabSideEffects(selectedTabClass);
    });
  }

  bindButtonClicks() {
    if (!this.tabButtonElements.length) {
      return;
    }

    this.tabButtonElements.forEach((tabButtonElement) => {
      tabButtonElement.addEventListener('click', (event) => {
        event.preventDefault();

        const selectedTabClass = tabButtonElement.getAttribute('data-tab');
        if (!selectedTabClass) {
          return;
        }

        this.setActiveTab(selectedTabClass, {
          updateSelect: true,
          syncGroup: true,
        });

        this.handleTabSideEffects(selectedTabClass);
      });
    });
  }

  handleTabSideEffects(selectedTabClass) {
    if (!isOnSearchPage()) {
      return;
    }

    const hasVehicle = hasVehicleSelection();

    if (hasVehicle) {
      window.Convermax?.discardVehicle?.();
      return;
    }

    if (selectedTabClass === VEHICLE_TAB_CLASS) {
      window.Convermax?.discardSearchFields?.([...this.options.tiresFields, ...this.options.wheelsFields]);
      return;
    }

    if (selectedTabClass === WHEEL_TAB_CLASS) {
      window.Convermax?.discardSearchFields?.(this.options.tiresFields);
      return;
    }

    if (selectedTabClass === TIRE_TAB_CLASS) {
      window.Convermax?.discardSearchFields?.(this.options.wheelsFields);
    }
  }

  setActiveTab(tabClass, settings = {}) {
    const shouldUpdateSelect = settings.updateSelect ?? true;
    const shouldSyncGroup = settings.syncGroup ?? false;

    this.tabContentElement.querySelectorAll('.cm_tab-content').forEach((tabPanelElement) => {
      tabPanelElement.classList.toggle('active', tabPanelElement.classList.contains(tabClass));
    });

    this.updateTabButtonsAria(tabClass);

    if (shouldUpdateSelect && this.tabSelectElement) {
      this.tabSelectElement.value = tabClass;
    }

    if (shouldSyncGroup) {
      this.options.syncActiveTab?.(tabClass, this);
    }
  }

  updateTabButtonsAria(tabClass) {
    if (!this.tabButtonElements.length) {
      return;
    }

    this.tabButtonElements.forEach((tabButtonElement) => {
      const buttonTabClass = tabButtonElement.getAttribute('data-tab');
      tabButtonElement.setAttribute('aria-selected', buttonTabClass === tabClass ? 'true' : 'false');
    });
  }
}

class ConvermaxYmmTabsManager {
  constructor(options) {
    this.options = options;
    this.instances = [];
  }

  init() {
    const rootElements = [...document.querySelectorAll(ROOT_SELECTOR)];
    if (!rootElements.length) {
      return;
    }

    const initialTabClass = getDefaultTabClass(this.options);

    rootElements.forEach((rootElement) => {
      if (rootElement.dataset.ymmTabsInit === '1') {
        return;
      }

      const instance = new ConvermaxYmmTabsInstance(rootElement, {
        ...this.options,
        syncActiveTab: this.syncActiveTab.bind(this),
      });

      instance.init(initialTabClass);

      rootElement.dataset.ymmTabsInit = '1';
      this.instances.push(instance);
    });
  }

  syncActiveTab(tabClass, sourceInstance) {
    this.instances.forEach((instance) => {
      if (instance === sourceInstance) {
        return;
      }

      instance.setActiveTab(tabClass, {
        updateSelect: true,
        syncGroup: false,
      });
    });
  }

  setActiveTabEverywhere(tabClass) {
    this.instances.forEach((instance) => {
      instance.setActiveTab(tabClass, {
        updateSelect: true,
        syncGroup: false,
      });
    });
  }

  handleVehicleSelected() {
    this.setActiveTabEverywhere(VEHICLE_TAB_CLASS);

    if (!isOnSearchPage()) {
      return;
    }

    const searchString = window.location.search || '';
    if (
      hasAnyFieldInSearch(
        searchString,
        this.options.tiresFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter'),
      )
    ) {
      window.Convermax?.discardSearchFields?.(this.options.tiresFields);
      return;
    }

    if (hasAnyFieldInSearch(searchString, this.options.wheelsFields)) {
      window.Convermax?.discardSearchFields?.(
        this.options.wheelsFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter'),
      );
    }
  }
}

function isOnSearchPage() {
  return window.document.body.classList.contains('cm_hasSearchPage');
}

function hasAnyFieldInSearch(searchString, fieldNames) {
  return fieldNames.some((fieldName) => searchString.includes(fieldName));
}

function hasAnyFieldInUrl(fieldNames) {
  return fieldNames.some((fieldName) => window.location.href.includes(fieldName));
}

function hasVehicleSelection() {
  return window.Convermax?.isVehicleSelected?.() ?? !!localStorage.getItem(VEHICLE_STORAGE_KEY);
}

function getStoredSelection(storageKey) {
  const storedValue = localStorage.getItem(storageKey);
  if (!storedValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) && parsedValue.length > 0 ? parsedValue : null;
  } catch {
    return null;
  }
}

function getDefaultTabClass(options) {
  const searchString = window.location.search || '';
  const tireFields = options.tiresFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter');
  const wheelFields = options.wheelsFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter');

  if (hasVehicleSelection() || hasAnyFieldInSearch(searchString, options.baseFitmentFields)) {
    return VEHICLE_TAB_CLASS;
  }

  if (hasAnyFieldInSearch(searchString, tireFields)) {
    return TIRE_TAB_CLASS;
  }

  if (hasAnyFieldInSearch(searchString, wheelFields)) {
    return WHEEL_TAB_CLASS;
  }

  return VEHICLE_TAB_CLASS;
}

function dispatchConvermaxDialogOpen() {
  window.document.dispatchEvent(
    new CustomEvent('locationVehicleModal:open', {
      detail: {
        modalId: 'location-vehicle-modal',
        activeTabId: 'tab-convermax',
      },
    }),
  );
}

function openConvermaxDialogWhenReady() {
  if (window.locationVehicleModalManager) {
    dispatchConvermaxDialogOpen();
    return;
  }

  window.document.addEventListener(
    'locationVehicleModal:ready',
    () => {
      dispatchConvermaxDialogOpen();
    },
    { once: true },
  );
}

function hasSelectionForTab(tabClass, options) {
  const searchString = window.location.search || '';

  if (tabClass === TIRE_TAB_CLASS) {
    const tireFields = options.tiresFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter');

    return hasAnyFieldInSearch(searchString, tireFields) || !!getStoredSelection(TIRE_STORAGE_KEY);
  }

  if (tabClass === WHEEL_TAB_CLASS) {
    const wheelFields = options.wheelsFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter');

    return hasAnyFieldInSearch(searchString, wheelFields) || !!getStoredSelection(WHEEL_STORAGE_KEY);
  }

  return false;
}

function getCollectionTabClass(collectionFitmentType) {
  if (collectionFitmentType === 'Wheel') {
    return WHEEL_TAB_CLASS;
  }

  if (collectionFitmentType === 'Tire') {
    return TIRE_TAB_CLASS;
  }

  return VEHICLE_TAB_CLASS;
}

function shouldOpenConvermaxDialogForCollection(collectionFitmentType, options) {
  if (!window.Convermax.isStoreSelected) {
    return false;
  }

  if (hasVehicleSelection()) {
    return false;
  }

  const collectionTabClass = getCollectionTabClass(collectionFitmentType);
  if (collectionTabClass === VEHICLE_TAB_CLASS) {
    return false;
  }

  return !hasSelectionForTab(collectionTabClass, options);
}

function syncTabsToCollection(collectionFitmentType) {
  if (!currentTabsManager) {
    return;
  }

  const collectionTabClass = getCollectionTabClass(collectionFitmentType);

  if (collectionTabClass === VEHICLE_TAB_CLASS) {
    currentTabsManager.setActiveTabEverywhere(VEHICLE_TAB_CLASS);
    return;
  }

  if (hasVehicleSelection()) {
    currentTabsManager.setActiveTabEverywhere(VEHICLE_TAB_CLASS);
    return;
  }

  if (hasSelectionForTab(collectionTabClass, currentTabsManager.options)) {
    currentTabsManager.setActiveTabEverywhere(collectionTabClass);
    return;
  }

  currentTabsManager.setActiveTabEverywhere(VEHICLE_TAB_CLASS);
}

function hasWheelOrTireSelection(options) {
  const searchString = window.location.search || '';
  const tireFields = options.tiresFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter');
  const wheelFields = options.wheelsFields.filter((fieldName) => fieldName !== 'wheel_tire_diameter');

  return (
    hasAnyFieldInSearch(searchString, tireFields) ||
    hasAnyFieldInSearch(searchString, wheelFields) ||
    !!getStoredSelection(TIRE_STORAGE_KEY) ||
    !!getStoredSelection(WHEEL_STORAGE_KEY)
  );
}

function hasKeywordQuery() {
  const searchParams = new URLSearchParams(window.location.search);
  const query = searchParams.get('query');

  return !!query?.trim();
}

function shouldOpenConvermaxDialog(options) {
  return (
    window.Convermax.isStoreSelected &&
    (window.Convermax.Shopify?.pageType === 'collection' ||
      (window.location.pathname === options.searchPageUrl && !hasKeywordQuery())) &&
    !hasVehicleSelection() &&
    !hasWheelOrTireSelection(options)
  );
}

export function getYmmTabPreselection(options) {
  const searchString = window.location.search || '';

  if (
    hasKeywordQuery() ||
    hasVehicleSelection() ||
    hasAnyFieldInSearch(searchString, options.tiresFields) ||
    hasAnyFieldInSearch(searchString, options.wheelsFields)
  ) {
    return [];
  }

  const pagePath = window.location.pathname;

  const preselectionConfig = [
    {
      url: options.tiresCollectionUrl,
      fields: options.tiresFields,
      storageKey: TIRE_STORAGE_KEY,
    },
    {
      url: options.wheelsCollectionUrl,
      fields: options.wheelsFields,
      storageKey: WHEEL_STORAGE_KEY,
    },
    {
      url: options.searchPageUrl,
      fields: options.tiresFields,
      storageKey: TIRE_STORAGE_KEY,
    },
    {
      url: options.searchPageUrl,
      fields: options.wheelsFields,
      storageKey: WHEEL_STORAGE_KEY,
    },
  ];

  for (const configItem of preselectionConfig) {
    const { url, fields, storageKey } = configItem;
    const storedSelection = getStoredSelection(storageKey);

    if (pagePath === url && !hasAnyFieldInUrl(fields) && storedSelection) {
      return storedSelection;
    }
  }

  if (window.Convermax.Shopify?.pageType === 'collection') {
    const tireSelection = getStoredSelection(TIRE_STORAGE_KEY);
    const wheelSelection = getStoredSelection(WHEEL_STORAGE_KEY);
    const combinedSelection = [];

    if (Array.isArray(tireSelection) && tireSelection.length > 0) {
      combinedSelection.push(...tireSelection);
    }

    if (Array.isArray(wheelSelection) && wheelSelection.length > 0) {
      combinedSelection.push(...wheelSelection);
    }

    if (combinedSelection.length > 0) {
      combinedSelection.push({ field: 'InitPreselection', term: 'True' });
      return combinedSelection;
    }
  }

  return [];
}

export function initYmmTabs(options) {
  currentTabsManager = new ConvermaxYmmTabsManager({
    baseFitmentFields: options.baseFitmentFields,
    tiresFields: options.tiresFields,
    wheelsFields: options.wheelsFields,
  });

  currentTabsManager.init();

  if (shouldOpenConvermaxDialog(currentTabsManager.options)) {
    openConvermaxDialogWhenReady();
  }

  return currentTabsManager;
}

export function ymmTabsOnVehicleSelected() {
  currentTabsManager?.handleVehicleSelected();
}

function removeWheelTireTab(headerSelector, contentSelector) {
  const headerElement = window.document.querySelector(headerSelector);
  const contentElement = window.document.querySelector(contentSelector);

  if (headerElement && contentElement) {
    headerElement.remove();
    contentElement.remove();
  }
}

export function ymmTabsResponseHandler(collectionFitmentType) {
  if (!currentTabsManager) {
    return;
  }

  if (collectionFitmentType === 'Wheel') {
    removeWheelTireTab(
      '#vehicle-ymm-tabs .cm_tabs_header option[value="cm_tire-search"]',
      '#vehicle-ymm-tabs .cm-home-tire',
    );
    removeWheelTireTab(
      '#location-vehicle-ymm-tabs .cm_tabs_header button[data-tab="cm_tire-search"]',
      '#location-vehicle-ymm-tabs .cm-home-tire',
    );
  } else if (collectionFitmentType === 'Tire') {
    removeWheelTireTab(
      '#vehicle-ymm-tabs .cm_tabs_header option[value="cm_wheel-search"]',
      '#vehicle-ymm-tabs .cm-home-wheel',
    );
    removeWheelTireTab(
      '#location-vehicle-ymm-tabs .cm_tabs_header button[data-tab="cm_wheel-search"]',
      '#location-vehicle-ymm-tabs .cm-home-wheel',
    );
  } else {
    return;
  }

  syncTabsToCollection(collectionFitmentType);

  if (shouldOpenConvermaxDialogForCollection(collectionFitmentType, currentTabsManager.options)) {
    openConvermaxDialogWhenReady();
  }
}
